import { useMemo, useState } from 'react'
import { MapPin, Package, Search, Truck } from 'lucide-react'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import type { SortAccessors } from '@/lib/tableSort'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { ColumnCells, ResizableTableHead } from '@/components/table'
import { Link, useSearchParams } from 'react-router-dom'
import { StorageTab } from '@/components/inventory/storage/StorageTab'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { PRODUCTS_TABS, type ProductTab } from '@/lib/pageTabs'
import { useAllCreatedProducts } from '@/hooks/useProducts'
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { PageHeading } from '@/components/layout/PageHeading'
import type { CreatedProduct } from '@/types/products'
import { usePermission, PERMISSIONS } from '@/lib/permissions'
import { filterProductsForShipping, isShippable, productLocation } from '@/lib/shipping'
import { ShipProductDialog } from '@/components/shipping/ShipProductDialog'
import { MoveProductDialog } from '@/components/inventory/storage/MoveProductDialog'
import { TruckFastIcon } from '@/components/shipping/TruckFastIcon'
import { ProductLabelsTable } from '@/components/labels/ProductLabelsTable'
import { Button } from '@/components/ui/button'
import { isSuperseded } from '@/lib/productSupersession'
import { useLocationText } from '@/hooks/useStoragePlaces'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { catalogName, divisionOfCode } from '@/lib/productRecord'
import { LINK, useCanOpenPatients } from '@/components/record/RecordParts'

/**
 * Product — where product is held, and what has been made.
 *
 * Its own page rather than a tab. Data Center → Products used to redirect to
 * `/inventory?tab=products`, a tab that no longer exists after the inventory
 * rebuild, so it silently fell back to Consumables and the entry looked broken.
 *
 * The material master (consumables, reagents, kits) stays under Inventory, and
 * the product-code catalogue stays under Configure → Products. This page is the
 * OPERATIONAL view: "where is it" (Storage), "what did we make" (Products) and
 * "what labels did we print" (Labels) — not "what may we make", which is the
 * admin catalogue.
 */

// Shared with the main menu (z8nrz7f7g3).
const TABS = PRODUCTS_TABS.map((t) => t.value)
/** Storage is the default: it is what this page did before the tab bar existed. */
const DEFAULT_TAB: ProductTab = 'storage'

export function ProductsPage() {
  const [params, setParams] = useSearchParams()
  const canOpenPatients = useCanOpenPatients()
  // Filtered fallback, not a bare `?? 'storage'`: an unknown or stale ?tab=
  // would otherwise select a tab that renders nothing.
  const requested = params.get('tab')
  const tab: ProductTab = TABS.includes(requested as ProductTab)
    ? (requested as ProductTab)
    : DEFAULT_TAB

  function setTab(next: string) {
    const sp = new URLSearchParams(params)
    sp.set('tab', next)
    setParams(sp, { replace: true })
  }

  return (
    <div className='min-h-full bg-muted/20'>
      <div className='w-full px-6 py-8'>
        <div className='mb-5 flex items-center gap-3'>
          <div className='flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary'>
            <Package className='h-5 w-5 text-primary-foreground' />
          </div>
          <PageHeading
            title='Product'
            description={
              tab === 'products'
                ? canOpenPatients
                  ? 'Every product, its patient and where it is stored. Click a DIN to open its full record, or a patient with an MRN to open their page.'
                  : 'Every product, its patient and where it is stored. Click a DIN to open its full record.'
                : 'Storage units and what is held in each one.'
            }
          />
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            {PRODUCTS_TABS.map((t) => (
              <TabsTrigger key={t.value} value={t.value}>
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value='storage' className='mt-6'>
            <StorageTab />
          </TabsContent>

          <TabsContent value='products' className='mt-6'>
            <CreatedProductsTab />
          </TabsContent>

          {/* Every printed product label, with its verification signatures (z8nrz7dt48). */}
          <TabsContent value='labels' className='mt-6'>
            <ProductLabelsTable />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}

// Per-user column layout. DIN and Actions are locked — DIN identifies the
// product, and Actions holds the ship icon. See src/components/table/index.ts.
const CREATED_PRODUCTS_TABLE_ID = 'products-created'

const CREATED_PRODUCTS_COLUMNS: TableColumnDef[] = [
  { id: 'din', header: 'DIN', locked: true, sortable: true },
  { id: 'productCode', header: 'Product code', sortable: true },
  { id: 'description', header: 'Description', sortable: true },
  { id: 'volume', header: 'Volume (mL)', sortable: true },
  { id: 'patient', header: 'Patient', sortable: true },
  { id: 'mrn', header: 'MRN', sortable: true },
  { id: 'storageLocation', header: 'Storage location', sortable: true },
  { id: 'created', header: 'Created', sortable: true },
  { id: 'actions', header: 'Actions', locked: true },
]

// Header sorting (z8nrz7e4h6). Unsorted, products stay newest first.
const CREATED_PRODUCTS_SORT: SortAccessors<CreatedProduct> = {
  din: p => p.din,
  productCode: p => p.productDescriptionCode,
  description: p => productDescription(p),
  volume: p => p.volumeMl,
  // By name: the MRN is the link and its own column, never the patient's label.
  patient: p => p.patientName ?? null,
  mrn: p => p.patientMrn,
  storageLocation: p => productLocation(p).text || null,
  created: p => p.createdAt,
}

/*
 * This table sized its columns as PERCENTAGES summing to 100, and those are
 * kept rather than converted to a `defaultWidth` in px: a percentage keeps
 * filling the container at any window width, where a fixed default would leave
 * a wide screen with a short table and a right-hand gap. A width the user drags
 * still overrides these inline (see ResizableTableHead), which is the point —
 * the percentages are a good default, not a verdict.
 */
const CREATED_PRODUCTS_HEAD_CLASSES: Record<string, string> = {
  din: 'w-[15%]',
  productCode: 'w-[9%]',
  description: 'w-[13%]',
  volume: 'w-[7%]',
  patient: 'w-[12%]',
  mrn: 'w-[9%]',
  storageLocation: 'w-[15%]',
  created: 'w-[12%]',
  actions: 'w-[8%]',
}

/**
 * The Description column: the catalog name and the division, "HPC, APHERESIS ·
 * Div A0" (z8nrz7g57f). The ISBT description's attributes after its first `|`
 * are on the product page.
 */
function productDescription(p: CreatedProduct): string | null {
  const name = catalogName(p.catalogDescription)
  const division = divisionOfCode(p.productDescriptionCode)
  if (!name) return null
  return division ? `${name} · Div ${division}` : name
}

/**
 * The products this tenant has created, newest first — searchable by DIN,
 * product code or location, and shippable from the truck icon in each row's
 * Actions (z8nrz7eaex). More products can be added inside the ship dialog.
 */
function CreatedProductsTab() {
  const cols = useTableColumns(CREATED_PRODUCTS_TABLE_ID, CREATED_PRODUCTS_COLUMNS)
  const { can } = usePermission()
  const canShip = can(PERMISSIONS.productsShip)
  const canMove = can(PERMISSIONS.productsManage)
  // The Patient cell links only for a reader who can open the patient page.
  const canOpenPatients = useCanOpenPatients()
  const locate = useLocationText()
  const { data, isLoading, error } = useAllCreatedProducts()
  const [q, setQ] = useState('')
  const filtered = useMemo(() => filterProductsForShipping(data ?? [], q, locate), [data, q, locate])
  const products = useSortedRows(filtered, cols.sort, CREATED_PRODUCTS_SORT)

  const [shipping, setShipping] = useState<CreatedProduct[] | null>(null)
  const [moving, setMoving] = useState<CreatedProduct | null>(null)

  return (
    <>
    <div className='mb-3'>
      <div className='relative w-full max-w-md'>
        <Search className='pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground' />
        <Input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder='Search by DIN, product code, patient, MRN or location…'
          aria-label='Search products'
          className='bg-white pl-8'
        />
      </div>
    </div>
    <div className='overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm'>
      <Table>
        <TableHeader>
          <TableRow>
            {cols.visible.map(({ def }) => (
              <ResizableTableHead
                key={def.id}
                columnId={def.id}
                columns={cols}
                label={def.header}
                className={CREATED_PRODUCTS_HEAD_CLASSES[def.id]}
                resizable={def.id !== 'actions'}
              >
                {def.header}
              </ResizableTableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <MessageRow span={cols.visible.length}>Loading…</MessageRow>
          ) : error ? (
            <MessageRow span={cols.visible.length}>Could not load products.</MessageRow>
          ) : (data?.length ?? 0) === 0 ? (
            <MessageRow span={cols.visible.length}>
              No products created yet. They appear here once an operator creates one from a
              patient form.
            </MessageRow>
          ) : products.length === 0 ? (
            <MessageRow span={cols.visible.length}>No products match your search.</MessageRow>
          ) : (
            products.map((p) => {
              const location = productLocation(p)
              const shippable = isShippable(p)
              // Re-coded (z8nrz7e72t): kept as history, greyed, and not shippable.
              const superseded = isSuperseded(p)
              return (
              <TableRow key={p.id} className={cn(superseded && 'opacity-60')}>
                <ColumnCells
                  columns={cols}
                  cells={{
                    din: (
                      <TableCell className='font-medium tabular-nums'>
                        {/* Each row opens ITS product: one DIN is several products (divisions, vials). */}
                        <Link className={cn(LINK, 'font-mono text-[12.5px]')} to={`/products/${p.id}`}>
                          {p.din}
                        </Link>
                      </TableCell>
                    ),
                    productCode: (
                      <TableCell>
                        <span className={cn(superseded && 'line-through')}>{p.productDescriptionCode}</span>
                        {superseded && (
                          <span className='ml-2 rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground'>
                            Superseded
                          </span>
                        )}
                      </TableCell>
                    ),
                    description: (
                      <TableCell>
                        {productDescription(p) ?? <span className='text-muted-foreground'>—</span>}
                      </TableCell>
                    ),
                    volume: (
                      <TableCell className='tabular-nums'>
                        {p.volumeMl == null ? <span className='text-muted-foreground'>—</span> : p.volumeMl}
                      </TableCell>
                    ),
                    patient: (
                      <TableCell>
                        {/* Kit forms carry no patient (`requiresPatientHistory: false`),
                            so an em-dash here is a real state, not missing data. The
                            patient's name, never the MRN: the MRN is the link's target
                            and has its own column. */}
                        {(() => {
                          const name = p.patientName?.trim() || null
                          if (!p.patientMrn) return name ?? '—'
                          if (!canOpenPatients) return name ?? <span className='italic text-muted-foreground'>Name not recorded</span>
                          return (
                            <Link
                              className={cn(LINK, !name && 'font-normal italic')}
                              to={`/patients/${encodeURIComponent(p.patientMrn)}`}
                              title={`Open the patient page (MRN ${p.patientMrn})`}
                            >
                              {name ?? 'Name not recorded'}
                            </Link>
                          )
                        })()}
                      </TableCell>
                    ),
                    mrn: (
                      <TableCell className='font-mono text-xs'>
                        {p.patientMrn ?? <span className='text-muted-foreground'>—</span>}
                      </TableCell>
                    ),
                    storageLocation: (
                      <TableCell>
                        {location.kind === 'stored' ? (
                          <>
                            <span className='mr-2 rounded-full border border-sky-300 bg-sky-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-sky-800'>
                              Stored
                            </span>
                            <span className='font-mono text-xs'>{locate(location.text)}</span>
                          </>
                        ) : location.kind === 'shipped' ? (
                          <span title={`Shipped to ${p.locationPartner?.name ?? location.text}`}>
                            <span className='mr-2 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800'>
                              Shipped
                            </span>
                            <span className='font-mono text-xs'>{location.text}</span>
                          </span>
                        ) : (
                          <span className='text-muted-foreground'>Not placed</span>
                        )}
                      </TableCell>
                    ),
                    created: (
                      <TableCell className='text-muted-foreground'>
                        {new Date(p.createdAt).toLocaleString()}
                      </TableCell>
                    ),
                    actions: (
                      <TableCell>
                        {/* Only a product that is in storage can be re-filed: a
                            shipped one has left (its location is the partner),
                            and an unplaced one is put away from its procedure. */}
                        {canMove && !superseded && location.kind === 'stored' && (
                          <Button
                            variant='ghost'
                            size='sm'
                            className='h-8 w-8 p-0'
                            title={`Move ${p.din}`}
                            aria-label={`Move ${p.din}`}
                            onClick={() => setMoving(p)}
                          >
                            <MapPin className='h-4 w-4' />
                          </Button>
                        )}
                        {canShip && !superseded && (
                          <Button
                            variant='ghost'
                            size='sm'
                            className='h-8 w-8 p-0'
                            title={shippable ? `Ship ${p.din}` : `Shipped to ${location.text} — reprint the shipping label`}
                            aria-label={shippable ? `Ship ${p.din}` : `Reprint the shipping label for ${p.din}`}
                            onClick={() => setShipping([p])}
                          >
                            {shippable ? <Truck className='h-4 w-4' /> : <TruckFastIcon className='h-5 w-5 text-emerald-600' />}
                          </Button>
                        )}
                      </TableCell>
                    ),
                  }}
                />
              </TableRow>
              )
            })
          )}
        </TableBody>
      </Table>
    </div>
    {(data?.length ?? 0) > 0 && (
      <p className='mt-3 text-xs text-muted-foreground'>
        Showing {products.length} {products.length === 1 ? 'product' : 'products'} · every DIN opens the
        product record{canOpenPatients ? '; a patient with an MRN opens the patient page' : ''}
      </p>
    )}
    <ShipProductDialog
      open={shipping !== null}
      onClose={() => setShipping(null)}
      initialProducts={shipping ?? []}
    />
    <MoveProductDialog product={moving} onClose={() => setMoving(null)} />
    </>
  )
}

// `span` is passed in rather than hard-coded: the column count is now a user
// preference, and a stale constant would leave this row short of the table.
function MessageRow({ children, span }: { children: React.ReactNode; span: number }) {
  return (
    <TableRow>
      <TableCell colSpan={span} className='py-12 text-center text-sm text-muted-foreground'>
        {children}
      </TableCell>
    </TableRow>
  )
}
